import React, { useState, useEffect, useContext, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import { AuthContext } from "../../context/Auth/AuthContext";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { PendingMessagesProvider } from "../../context/PendingMessages/PendingMessagesContext";
import toastError from "../../errors/toastError";

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [typing, setTyping] = useState(false);
  // Presencia de OTROS agentes en esta conversación (excluye al usuario actual).
  const [otherViewers, setOtherViewers] = useState([]);
  // Nombre del agente que está respondiendo ahora mismo (o null).
  const [agentTyping, setAgentTyping] = useState(null);
  const agentTypingTimer = useRef(null);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/tickets/" + ticketId);
        setContact(data.contact);
        setTicket(data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    const joinRoom = () => socket.emit("joinChatBox", ticketId);
    const handleTicket = async (data) => {
      if (data.action === "update" && data.ticket?.id === +ticketId) setTicket(data.ticket);
      if (data.action === "delete" && data.ticketId === +ticketId) {
        try {
          await api.get("/tickets/" + ticketId);
        } catch (err) {
          toast.success("Ticket deleted successfully.");
          history.push("/tickets");
        }
      }
    };
    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prev) => prev.id === data.contact?.id ? { ...prev, ...data.contact } : prev);
      }
    };
    const handleContactTyping = (data) => {
      if (data.ticketId === +ticketId)
        setTyping(["typing", "composing", "recording"].includes(data.action));
    };
    const handleTicketPresence = (data) => {
      if (String(data.ticketId) !== String(ticketId)) return;
      const others = (data.viewers || []).filter((v) => v.userId !== user?.id);
      setOtherViewers(others);
    };
    const handleAgentTyping = (data) => {
      if (String(data.ticketId) !== String(ticketId)) return;
      if (data.userId === user?.id) return;
      setAgentTyping(data.userName || null);
      if (agentTypingTimer.current) clearTimeout(agentTypingTimer.current);
      // No hay evento de "dejó de escribir"; se limpia por inactividad.
      agentTypingTimer.current = setTimeout(() => setAgentTyping(null), 5000);
    };

    socket.on("connect", joinRoom);
    socket.on("reconnect", joinRoom);
    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);
    socket.on("contactTyping", handleContactTyping);
    socket.on("ticketPresence", handleTicketPresence);
    socket.on("agentTyping", handleAgentTyping);

    if (socket.connected) {
      joinRoom();
    }

    return () => {
      socket.emit("leaveChatBox", ticketId);
      socket.off("connect", joinRoom);
      socket.off("reconnect", joinRoom);
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
      socket.off("contactTyping", handleContactTyping);
      socket.off("ticketPresence", handleTicketPresence);
      socket.off("agentTyping", handleAgentTyping);
      if (agentTypingTimer.current) clearTimeout(agentTypingTimer.current);
      setOtherViewers([]);
      setAgentTyping(null);
    };
  }, [ticketId, history, user?.id]);

  return (
    <div className="flex h-full overflow-hidden relative" id="drawer-container">
      <div
        className={`flex flex-col flex-1 overflow-hidden transition-[margin-right] duration-200 ease-in-out ${drawerOpen ? "mr-80" : "mr-0"}`}
      >
        <TicketHeader loading={loading}>
          <div className="flex-1 min-w-0">
            <TicketInfo contact={contact} ticket={ticket} onClick={() => setDrawerOpen(true)} typing={typing} otherViewers={otherViewers} agentTyping={agentTyping} />
          </div>
          <div className="flex-shrink-0">
            <TicketActionButtons ticket={ticket} />
          </div>
        </TicketHeader>
        <PendingMessagesProvider>
          <ReplyMessageProvider>
            <MessagesList ticketId={ticketId} isGroup={ticket.isGroup} channel={ticket.whatsapp?.channel} />
            <MessageInput ticketStatus={ticket.status} ticket={ticket} />
          </ReplyMessageProvider>
        </PendingMessagesProvider>
      </div>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={() => setDrawerOpen(false)}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
